/**
 * Image Component for Puck Editor
 * 
 * Gambar dengan berbagai opsi styling
 */

import type { ComponentConfig } from '@measured/puck'

export interface ImageProps {
  src: string
  alt: string  // Required for WCAG accessibility compliance
  width: 'auto' | 'full' | 'half' | 'third'
  alignment: 'left' | 'center' | 'right'
  rounded: 'none' | 'small' | 'medium' | 'large' | 'full'
  shadow: 'none' | 'small' | 'medium' | 'large'
  caption?: string
}

const widthClasses = {
  auto: 'w-auto max-w-full',
  full: 'w-full',
  half: 'w-1/2',
  third: 'w-1/3',
}

const alignmentClasses = {
  left: 'mr-auto',
  center: 'mx-auto',
  right: 'ml-auto',
}

const roundedClasses = {
  none: 'rounded-none',
  small: 'rounded',
  medium: 'rounded-lg',
  large: 'rounded-xl',
  full: 'rounded-full',
}

const shadowClasses = {
  none: '',
  small: 'shadow-sm',
  medium: 'shadow-md',
  large: 'shadow-lg',
}

export function Image({
  src,
  alt,
  width,
  alignment,
  rounded,
  shadow,
  caption,
}: ImageProps) {
  if (!src) {
    return (
      <div
        className={`bg-gray-200 flex items-center justify-center ${widthClasses[width]} ${alignmentClasses[alignment]} ${roundedClasses[rounded]} aspect-video`}
      >
        <span className="text-gray-400">Tambahkan URL gambar</span>
      </div>
    )
  }

  return (
    <figure className={`${alignmentClasses[alignment]} ${widthClasses[width]}`}>
      <img
        src={src}
        alt={alt || ''}
        className={`w-full ${roundedClasses[rounded]} ${shadowClasses[shadow]} object-cover`}
        loading="lazy"
      />
      {caption && (
        <figcaption className="mt-2 text-sm text-gray-500 text-center">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}

export const ImageConfig: ComponentConfig<ImageProps> = {
  label: 'Gambar',
  defaultProps: {
    src: '',
    alt: 'Deskripsi gambar',  // Default to encourage user to provide meaningful alt
    width: 'full',
    alignment: 'center',
    rounded: 'medium',
    shadow: 'none',
    caption: undefined,
  },
  fields: {
    src: {
      type: 'text',
      label: 'URL Gambar',
    },
    alt: {
      type: 'text',
      label: 'Deskripsi (Alt Text)',
    },
    width: {
      type: 'select',
      label: 'Lebar',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'Penuh', value: 'full' },
        { label: 'Setengah', value: 'half' },
        { label: 'Sepertiga', value: 'third' },
      ],
    },
    alignment: {
      type: 'radio',
      label: 'Posisi',
      options: [
        { label: 'Kiri', value: 'left' },
        { label: 'Tengah', value: 'center' },
        { label: 'Kanan', value: 'right' },
      ],
    },
    rounded: {
      type: 'select',
      label: 'Sudut',
      options: [
        { label: 'Tidak Ada', value: 'none' },
        { label: 'Kecil', value: 'small' },
        { label: 'Sedang', value: 'medium' },
        { label: 'Besar', value: 'large' },
        { label: 'Bulat', value: 'full' },
      ],
    },
    shadow: {
      type: 'select',
      label: 'Bayangan',
      options: [
        { label: 'Tidak Ada', value: 'none' },
        { label: 'Kecil', value: 'small' },
        { label: 'Sedang', value: 'medium' },
        { label: 'Besar', value: 'large' },
      ],
    },
    caption: {
      type: 'text',
      label: 'Keterangan',
    },
  },
  render: Image,
}
